import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/api/supabase'
import { USE_MOCK } from '@/config'
import type { NotificationTemplateRow } from '@/api/notifications'

const QK = ['notification-templates'] as const

export function useTemplates() {
  return useQuery({
    queryKey: QK,
    queryFn: async (): Promise<NotificationTemplateRow[]> => {
      if (USE_MOCK) return []
      const supabase = createClient()
      const { data, error } = await supabase
        .from('notification_templates')
        .select('event_type, role, is_enabled, title, body')
        .neq('event_type', 'test.ping')
      if (error) throw new Error(error.message)
      return (data ?? []) as NotificationTemplateRow[]
    },
  })
}

export interface TemplateUpdatePayload {
  event_type: string
  role: string
  is_enabled?: boolean
  title?: string
  body?: string
}

export function useUpdateTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: TemplateUpdatePayload) => {
      if (USE_MOCK) return
      const supabase = createClient()
      const { data: me } = await supabase.auth.getUser()
      const update: Record<string, unknown> = {}
      if (payload.is_enabled !== undefined) update.is_enabled = payload.is_enabled
      if (payload.title !== undefined) update.title = payload.title
      if (payload.body !== undefined) update.body = payload.body
      if (me.user?.id) update.updated_by = me.user.id
      const { error } = await supabase
        .from('notification_templates')
        .update(update)
        .eq('event_type', payload.event_type)
        .eq('role', payload.role)
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK })
    },
  })
}

export function useSetAllRolesEnabled() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ event_type, enabled }: { event_type: string; enabled: boolean }) => {
      if (USE_MOCK) return
      const supabase = createClient()
      const { data: me } = await supabase.auth.getUser()
      const update: Record<string, unknown> = { is_enabled: enabled }
      if (me.user?.id) update.updated_by = me.user.id
      const { error } = await supabase
        .from('notification_templates')
        .update(update)
        .eq('event_type', event_type)
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK })
    },
  })
}
